<template>
  <div
    class="relative w-full overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 shadow-2xl transition-all duration-500 dark:border-white/10 dark:bg-[#09090b] sm:rounded-[32px]">
    <header
      class="relative z-20 flex h-16 items-center justify-between border-b border-zinc-200 bg-white/80 px-4 backdrop-blur-md transition-colors duration-300 dark:border-white/5 dark:bg-[#0c0c0e]/80 sm:px-6">
      <div class="flex items-center gap-3">
        <div
          class="group flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 transition-transform duration-500 hover:rotate-12 hover:scale-110">
          <span class="text-xs font-bold tracking-tighter">NG</span>
        </div>
        <div class="min-w-0">
          <h2 class="text-sm font-bold tracking-tight text-zinc-900 truncate transition-colors dark:text-zinc-100">
            NexGrid <span class="hidden text-zinc-400 sm:inline">Engine</span>
          </h2>
          <p class="hidden text-[10px] text-zinc-500 sm:block">Semantic Token Mapping</p>
        </div>
      </div>

      <div class="flex items-center gap-3 sm:gap-4">

        <router-link to="/dashboard/hub"
          class="group relative flex h-9 w-9 items-center justify-center rounded-xl border border-zinc-200/50 bg-zinc-100/50 text-zinc-500 transition-all duration-300 hover:-translate-y-0.5 hover:border-zinc-300 hover:bg-white hover:text-indigo-600 hover:shadow-sm dark:border-white/5 dark:bg-white/5 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-white"
          title="Tutorial Hub">
          <span
            class="material-symbols-rounded text-[20px] transition-transform duration-300 group-hover:rotate-12">school</span>
        </router-link>

        <div
          class="flex shrink-0 items-center gap-1 overflow-x-auto bg-zinc-100/50 p-1 rounded-xl border border-zinc-200/50 backdrop-blur-sm transition-colors dark:bg-white/5 dark:border-white/5 max-w-[200px] sm:max-w-none no-scrollbar">
          <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
            class="relative whitespace-nowrap px-3 py-1.5 text-[10px] font-bold tracking-wide rounded-lg transition-all duration-300 ease-out"
            :class="activeTab === tab.id
              ? 'bg-white text-zinc-900 shadow-md dark:bg-[#27272a] dark:text-white dark:shadow-black/20 scale-100'
              : 'text-zinc-500 hover:text-zinc-700 hover:bg-black/5 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-white/5 scale-95 opacity-80 hover:opacity-100 hover:scale-100'">
            {{ tab.label }}
          </button>
        </div>
      </div>
    </header>

    <div class="flex flex-col overflow-hidden lg:grid lg:h-[800px] lg:grid-cols-[320px_1fr] xl:grid-cols-[340px_1fr]">

      <aside
        class="relative z-10 flex flex-col gap-6 border-b border-zinc-200 bg-white p-4 transition-colors duration-300 dark:border-white/5 dark:bg-[#0c0c0e] lg:border-b-0 lg:border-r lg:p-6 lg:overflow-y-auto">

        <div class="space-y-3 animate-stagger-1">
          <div class="flex items-center justify-between">
            <span class="text-[11px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">Reference
              Palette</span>
            <span class="rounded-full bg-zinc-100 px-2 py-0.5 text-[9px] font-medium text-zinc-500 dark:bg-white/5">{{
              paletteColors.length }}</span>
          </div>
          <div class="flex flex-wrap gap-2 lg:grid lg:grid-cols-6">
            <div v-for="(c, i) in paletteColors" :key="'pal-' + c.id"
              class="group relative h-8 w-8 lg:h-auto lg:w-auto aspect-square overflow-hidden rounded-full border border-black/5 ring-2 ring-transparent transition-all duration-300 hover:scale-125 hover:z-10 hover:shadow-lg dark:border-white/10"
              :style="{ backgroundColor: c.hex, animationDelay: `${i * 50}ms` }" :title="c.name">
              <div class="absolute inset-0 opacity-0 transition-opacity group-hover:opacity-20 bg-white"></div>
            </div>
          </div>
        </div>

        <hr class="border-zinc-100 transition-colors dark:border-white/5" />

        <div class="space-y-8 pb-10">
          <div v-for="(categoryRoles, catName, idx) in rolesByCategory" :key="catName"
            class="space-y-3 animate-stagger-2" :style="{ animationDelay: `${idx * 100}ms` }">
            <div class="flex items-center gap-2">
              <span class="h-px flex-1 bg-zinc-100 dark:bg-white/5"></span>
              <span class="text-[10px] font-bold uppercase tracking-widest text-zinc-900 dark:text-zinc-200">{{ catName
                }}</span>
              <span class="h-px flex-1 bg-zinc-100 dark:bg-white/5"></span>
            </div>

            <div class="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-1">
              <div v-for="role in categoryRoles" :key="role.id"
                class="group relative flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-2.5 transition-all duration-200 hover:border-zinc-300 hover:shadow-md hover:-translate-y-0.5 dark:border-white/5 dark:bg-white/[0.02] dark:hover:border-white/10 dark:hover:bg-white/[0.04]">

                <div
                  class="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-black/5 shadow-sm transition-transform group-hover:scale-110 dark:border-white/10">
                  <input type="color" :value="getHexForRole(role.id)" @input="assign(role.id, $event.target.value)"
                    class="absolute -top-1/2 -left-1/2 h-[200%] w-[200%] cursor-pointer p-0 opacity-0" />
                  <div class="absolute inset-0 pointer-events-none"
                    :style="{ backgroundColor: getHexForRole(role.id) }"></div>
                </div>

                <div class="flex flex-1 flex-col justify-center min-w-0">
                  <span class="text-[11px] font-bold text-zinc-700 transition-colors dark:text-zinc-200 truncate">{{
                    role.label }}</span>
                  <span
                    class="text-[9px] font-mono text-zinc-400 truncate opacity-70 transition-opacity group-hover:opacity-100">{{
                    role.token }}</span>
                </div>

                <div class="opacity-0 group-hover:opacity-100 transition-opacity absolute right-2 text-zinc-400">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z">
                    </path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main
        class="relative flex flex-col bg-zinc-50/50 p-4 transition-colors duration-500 dark:bg-[#050505] sm:p-6 lg:overflow-y-auto custom-scrollbar">

        <transition name="tab-slide" mode="out-in">
          <div v-if="activeTab === 'preview'" key="preview" class="flex flex-col gap-8 pb-10">

            <div
              class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition-all duration-500 dark:border-white/10 dark:bg-[#0c0c0e]">
              <div
                class="flex h-10 items-center justify-between border-b border-zinc-100 bg-zinc-50 px-4 dark:border-white/5 dark:bg-white/[0.02]">
                <div class="flex gap-1.5">
                  <div class="h-2.5 w-2.5 rounded-full bg-red-400/20"></div>
                  <div class="h-2.5 w-2.5 rounded-full bg-amber-400/20"></div>
                  <div class="h-2.5 w-2.5 rounded-full bg-emerald-400/20"></div>
                </div>
                <span class="text-[9px] font-bold uppercase tracking-widest text-zinc-400">Layout Composition</span>
              </div>

              <div class="p-4 sm:p-8 transition-colors duration-500 min-h-[400px]"
                :style="{ backgroundColor: getHexForRole('surface-base') }">
                <nav
                  class="mb-6 sm:mb-8 flex items-center justify-between rounded-xl border p-3 sm:p-4 shadow-sm transition-all duration-500 animate-slide-up"
                  :style="{ backgroundColor: getHexForRole('surface-header'), borderColor: getHexForRole('border-subtle') }">
                  <div class="flex items-center gap-3">
                    <div
                      class="flex h-8 w-8 items-center justify-center rounded-lg shadow-sm transition-colors duration-300"
                      :style="{ backgroundColor: getHexForRole('primary'), color: getTextColorForHex(getHexForRole('primary')) }">
                      <span class="font-bold text-xs">NG</span>
                    </div>
                    <span class="text-sm font-bold tracking-tight transition-colors duration-300"
                      :style="{ color: getHexForRole('text-high') }">Analytics</span>
                  </div>
                  <div class="flex gap-2">
                    <div class="h-8 w-8 rounded-full border-2 transition-colors duration-300"
                      :style="{ borderColor: getHexForRole('surface-base') }">
                      <div class="h-full w-full rounded-full transition-colors duration-300"
                        :style="{ backgroundColor: getHexForRole('accent') }"></div>
                    </div>
                  </div>
                </nav>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">

                  <div
                    class="col-span-1 sm:col-span-2 rounded-xl border p-4 sm:p-6 shadow-sm transition-all duration-500 animate-slide-up delay-100 hover:shadow-md"
                    :style="{ backgroundColor: getHexForRole('surface-elevated'), borderColor: getHexForRole('border-subtle') }">
                    <div class="mb-6 flex items-center justify-between">
                      <div>
                        <h3 class="text-sm font-bold transition-colors duration-300"
                          :style="{ color: getHexForRole('text-high') }">Revenue</h3>
                        <p class="text-[10px] mt-1 transition-colors duration-300"
                          :style="{ color: getHexForRole('text-low') }">vs last month</p>
                      </div>
                      <span class="rounded-md px-2 py-1 text-[10px] font-bold transition-colors duration-300"
                        :style="{ backgroundColor: getHexForRole('success'), color: getTextColorForHex(getHexForRole('success')) }">
                        +12.4%
                      </span>
                    </div>
                    <div class="flex h-24 sm:h-32 items-end gap-2 sm:gap-3 opacity-90">
                      <div v-for="(h, idx) in [40, 70, 45, 90, 60, 85]" :key="h"
                        class="flex-1 rounded-t-sm transition-all duration-700 ease-out origin-bottom hover:opacity-80"
                        :style="{ height: h + '%', backgroundColor: getHexForRole('primary'), transitionDelay: `${idx * 50}ms` }">
                      </div>
                    </div>
                  </div>

                  <div
                    class="rounded-xl border p-4 shadow-sm flex flex-col gap-3 transition-all duration-500 animate-slide-up delay-200 hover:shadow-md"
                    :style="{ backgroundColor: getHexForRole('surface-subtle'), borderColor: getHexForRole('border-subtle') }">
                    <h3 class="text-xs font-bold transition-colors duration-300"
                      :style="{ color: getHexForRole('text-medium') }">Activity</h3>
                    <div v-for="(i, idx) in 3" :key="i"
                      class="flex items-center gap-3 rounded-lg border p-2 transition-all duration-300 hover:scale-[1.02]"
                      :style="{ backgroundColor: getHexForRole('surface-elevated'), borderColor: getHexForRole('border-subtle'), transitionDelay: `${idx * 100}ms` }">
                      <div class="h-2 w-2 rounded-full transition-colors duration-300"
                        :style="{ backgroundColor: i === 1 ? getHexForRole('warning') : getHexForRole('text-low') }">
                      </div>
                      <div class="h-1.5 w-full rounded-full transition-colors duration-300"
                        :style="{ backgroundColor: getHexForRole('text-low'), opacity: 0.3 }"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">

              <div class="flex flex-col gap-4 animate-slide-up delay-300">
                <h3 class="text-xs font-bold uppercase tracking-wider text-zinc-400">Content & Typography</h3>
                <div
                  class="rounded-2xl border p-4 sm:p-6 flex flex-col gap-6 transition-colors duration-500 shadow-sm hover:shadow-md"
                  :style="{ backgroundColor: getHexForRole('surface-elevated'), borderColor: getHexForRole('border-subtle') }">
                  <div class="space-y-2">
                    <h1 class="text-xl sm:text-2xl font-bold transition-colors duration-300"
                      :style="{ color: getHexForRole('text-high') }">Heading 1</h1>
                    <h2 class="text-base sm:text-lg font-semibold transition-colors duration-300"
                      :style="{ color: getHexForRole('text-medium') }">Heading 2 Subtitle</h2>
                    <p class="text-xs sm:text-sm transition-colors duration-300 leading-relaxed"
                      :style="{ color: getHexForRole('text-low') }">Body text showing low contrast description for
                      secondary information hierarchies within the application interface.</p>
                  </div>
                  <hr class="transition-colors duration-300" :style="{ borderColor: getHexForRole('border-subtle') }">
                  <div class="space-y-2">
                    <label class="text-[10px] font-bold uppercase tracking-wider transition-colors duration-300"
                      :style="{ color: getHexForRole('text-medium') }">Label</label>
                    <div
                      class="flex items-center justify-between rounded-lg border px-3 py-2.5 transition-colors duration-300"
                      :style="{ backgroundColor: getHexForRole('surface-base'), borderColor: getHexForRole('border-subtle') }">
                      <span class="text-xs transition-colors duration-300"
                        :style="{ color: getHexForRole('text-high') }">Input text value</span>
                      <div class="h-3 w-3 rounded-full opacity-20"
                        :style="{ backgroundColor: getHexForRole('text-low') }"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-col gap-4 animate-slide-up delay-400">
                <h3 class="text-xs font-bold uppercase tracking-wider text-zinc-400">Interactive & States</h3>
                <div
                  class="rounded-2xl border p-4 sm:p-6 flex flex-col gap-6 transition-colors duration-500 shadow-sm hover:shadow-md"
                  :style="{ backgroundColor: getHexForRole('surface-base'), borderColor: getHexForRole('border-subtle') }">
                  <div class="flex flex-wrap gap-3">
                    <button
                      class="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold shadow-sm whitespace-nowrap transition-transform active:scale-95 hover:shadow-md hover:-translate-y-0.5"
                      :style="{ backgroundColor: getHexForRole('primary'), color: getTextColorForHex(getHexForRole('primary')) }">
                      Primary Action
                    </button>
                    <button
                      class="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold whitespace-nowrap transition-transform active:scale-95 hover:-translate-y-0.5"
                      :style="{ backgroundColor: getHexForRole('primary-soft'), color: getHexForRole('primary') }">
                      Soft Button
                    </button>
                  </div>

                  <div class="space-y-2.5">
                    <div v-for="state in ['danger', 'warning', 'success']" :key="state"
                      class="flex items-center gap-3 rounded-lg border p-3 border-l-4 transition-all duration-300 hover:pl-4"
                      :style="{
                        backgroundColor: getHexForRole('surface-elevated'),
                        borderColor: getHexForRole('border-subtle'),
                        borderLeftColor: getHexForRole(state)
                      }">
                      <div class="h-2 w-2 rounded-full transition-colors duration-300"
                        :style="{ backgroundColor: getHexForRole(state) }"></div>
                      <span class="text-xs font-medium transition-colors duration-300"
                        :style="{ color: getHexForRole('text-high') }">
                        {{ state.charAt(0).toUpperCase() + state.slice(1) }} System Message
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'tonal'" key="tonal" class="grid grid-cols-1 gap-6 xl:grid-cols-2 pb-10">
            <div v-for="(scale, idx) in tonalScales" :key="scale.id"
              class="rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5 shadow-sm transition-all duration-500 hover:shadow-md dark:border-white/5 dark:bg-[#0c0c0e] animate-slide-up"
              :style="{ animationDelay: `${idx * 100}ms` }">
              <div class="mb-5 flex items-center justify-between">
                <div class="flex items-center gap-4">
                  <div
                    class="h-12 w-12 rounded-2xl shadow-sm border border-black/5 flex items-center justify-center text-xs font-bold transition-transform hover:scale-110 hover:rotate-6"
                    :style="{ backgroundColor: scale.baseHex, color: getTextColorForHex(scale.baseHex) }">
                    500
                  </div>
                  <div>
                    <h4 class="text-sm font-bold text-zinc-900 dark:text-zinc-100">{{ scale.label }}</h4>
                    <div class="flex items-center gap-2 mt-0.5">
                      <span class="inline-block h-1.5 w-1.5 rounded-full"
                        :style="{ backgroundColor: scale.baseHex }"></span>
                      <p class="text-[10px] font-mono text-zinc-400">{{ scale.baseHex }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="space-y-2">
                <div class="overflow-x-auto pb-4 -mb-2 custom-scrollbar">
                  <div
                    class="flex h-16 min-w-[340px] w-full overflow-hidden rounded-xl border border-black/5 shadow-sm dark:border-white/5">
                    <div v-for="step in tonalSteps" :key="step"
                      class="group relative flex-1 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 hover:flex-[2.5] hover:z-10"
                      :style="{ backgroundColor: scale.steps[step] }">

                      <span
                        class="absolute opacity-0 transform translate-y-2 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 text-[10px] font-bold"
                        :style="{ color: getTextColorForHex(scale.steps[step]) }">
                        {{ step }}
                      </span>
                      <span class="text-[8px] opacity-40 group-hover:opacity-0 transition-opacity"
                        :style="{ color: getTextColorForHex(scale.steps[step]) }">
                        {{ step }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="flex justify-between px-1">
                  <span
                    class="text-[9px] font-bold tracking-widest uppercase text-zinc-300 dark:text-zinc-600">Lightest</span>
                  <span
                    class="text-[9px] font-bold tracking-widest uppercase text-zinc-300 dark:text-zinc-600">Darkest</span>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'code'" key="code" class="flex flex-col h-full min-h-[400px]">
            <div class="mb-4 flex gap-2 animate-slide-down">
              <button v-for="mode in ['css', 'tailwind', 'json']" :key="mode" @click="exportMode = mode"
                class="rounded-lg border border-zinc-200 bg-white px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-600 transition-all duration-200 hover:bg-zinc-50 hover:scale-105 active:scale-95 dark:border-white/5 dark:bg-white/5 dark:text-zinc-400 dark:hover:bg-white/10"
                :class="{ 'ring-2 ring-zinc-900 dark:ring-zinc-100 text-zinc-900 dark:text-white': exportMode === mode }">
                {{ mode }}
              </button>
            </div>
            <div
              class="flex-1 overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-900 shadow-inner dark:border-white/10 dark:bg-[#050505] animate-scale-in">
              <div class="flex h-8 items-center gap-1.5 border-b border-white/10 bg-white/5 px-4">
                <div class="h-2.5 w-2.5 rounded-full bg-zinc-700"></div>
                <div class="h-2.5 w-2.5 rounded-full bg-zinc-700"></div>
                <div class="h-2.5 w-2.5 rounded-full bg-zinc-700"></div>
              </div>
              <pre
                class="h-[calc(100%-32px)] overflow-auto p-4 sm:p-6 font-mono text-[10px] sm:text-[11px] leading-relaxed text-zinc-300 custom-scrollbar selection:bg-indigo-500/30">{{ exportContent }}</pre>
            </div>
          </div>
        </transition>

      </main>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'

const props = defineProps({
  palette: {
    type: Array,
    default: () => [
      { hex: '#0F172A', name: 'Midnight' },
      { hex: '#6366f1', name: 'Indigo' },
      { hex: '#10B981', name: 'Emerald' },
      { hex: '#F59E0B', name: 'Amber' },
      { hex: '#EF4444', name: 'Rose' },
      { hex: '#09090b', name: 'Deep' },
      { hex: '#71717a', name: 'Zinc' }
    ]
  }
})

const activeTab = ref('preview')
const exportMode = ref('css')
const tabs = [
  { id: 'preview', label: 'Preview' },
  { id: 'tonal', label: 'Tonal Maps' },
  { id: 'code', label: 'Export' }
]

const roles = [
  { id: 'surface-base', label: 'App Background', token: '--bg-base', category: 'Surfaces', defaultIndex: 5 },
  { id: 'surface-elevated', label: 'Card Surface', token: '--bg-surface', category: 'Surfaces', defaultIndex: 0 },
  { id: 'surface-header', label: 'Nav Header', token: '--bg-header', category: 'Surfaces', defaultIndex: 5 },
  { id: 'surface-subtle', label: 'Subtle Layer', token: '--bg-subtle', category: 'Surfaces', defaultIndex: 0 },
  { id: 'border-subtle', label: 'Border Line', token: '--border-subtle', category: 'Surfaces', defaultIndex: 6 },
  { id: 'primary', label: 'Primary Brand', token: '--color-primary', category: 'Brand', defaultIndex: 1 },
  { id: 'primary-soft', label: 'Primary Tint', token: '--color-primary-soft', category: 'Brand', defaultIndex: 1 },
  { id: 'accent', label: 'Accent Pop', token: '--color-accent', category: 'Brand', defaultIndex: 3 },
  { id: 'success', label: 'Success State', token: '--color-success', category: 'Feedback', defaultIndex: 2 },
  { id: 'warning', label: 'Warning State', token: '--color-warning', category: 'Feedback', defaultIndex: 3 },
  { id: 'danger', label: 'Error State', token: '--color-danger', category: 'Feedback', defaultIndex: 4 },
  { id: 'text-high', label: 'Text High', token: '--text-high', category: 'Typography', defaultIndex: 6 },
  { id: 'text-medium', label: 'Text Medium', token: '--text-medium', category: 'Typography', defaultIndex: 6 },
  { id: 'text-low', label: 'Text Low', token: '--text-low', category: 'Typography', defaultIndex: 6 },
]

const rolesByCategory = computed(() => {
  return roles.reduce((acc, role) => {
    if (!acc[role.category]) acc[role.category] = []
    acc[role.category].push(role)
    return acc
  }, {})
})

const assignments = reactive({})
const paletteColors = ref((props.palette || []).map((c, i) => ({
  id: i, hex: normalizeHex(c.hex), name: c.name
})))

roles.forEach(role => {
  const defaultColor = paletteColors.value[role.defaultIndex]?.hex || '#000000'
  assignments[role.id] = defaultColor
})

function normalizeHex(val) {
  if (!val) return ''
  let v = String(val).trim()
  if (!v.startsWith('#')) v = `#${v}`
  return v.toUpperCase().slice(0, 7)
}

function assign(roleId, hex) {
  assignments[roleId] = normalizeHex(hex)
}

function getHexForRole(roleId) {
  return assignments[roleId] || '#000000'
}

function hexToRgb(hex) {
  const v = normalizeHex(hex)
  if (v.length !== 7) return { r: 0, g: 0, b: 0 }
  const r = parseInt(v.slice(1, 3), 16)
  const g = parseInt(v.slice(3, 5), 16)
  const b = parseInt(v.slice(5, 7), 16)
  return { r, g, b }
}

function rgbToHex(r, g, b) {
  const toHex = n => n.toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

function getTextColorForHex(hex) {
  const { r, g, b } = hexToRgb(hex)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 150 ? '#09090b' : '#ffffff'
}

const tonalSteps = [10, 20, 30, 40, 50, 60, 70, 80, 90]

const tonalScales = computed(() => {
  const targets = ['primary', 'accent', 'success', 'warning', 'danger']
  const mapped = targets.map(id => {
    const base = getHexForRole(id)
    return {
      id,
      label: roles.find(r => r.id === id)?.label,
      baseHex: base,
      steps: generateScale(base)
    }
  })
  const neutralBase = getHexForRole('surface-base')
  mapped.push({
    id: 'neutral',
    label: 'Neutral / Grays',
    baseHex: neutralBase,
    steps: generateScale(neutralBase)
  })
  return mapped
})

function generateScale(hex) {
  const scale = {}
  const { r, g, b } = hexToRgb(hex)
  const { h, s } = rgbToHsl(r, g, b)
  tonalSteps.forEach(step => {
    const light = 0.95 - (step * 0.9) / 100
    const { r: nr, g: ng, b: nb } = hslToRgb(h, s, light)
    scale[step] = rgbToHex(nr, ng, nb)
  })
  return scale
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h, s, l };
}

function hslToRgb(h, s, l) {
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}

const exportContent = computed(() => {
  if (exportMode.value === 'css') {
    return `:root {\n${roles.map(r => `  ${r.token}: ${getHexForRole(r.id)};`).join('\n')}\n}`
  } else if (exportMode.value === 'tailwind') {
    const obj = {}; roles.forEach(r => obj[r.token.replace('--', '')] = getHexForRole(r.id));
    return `module.exports = {\n  theme: {\n    extend: {\n      colors: ${JSON.stringify(obj, null, 4)}\n    }\n  }\n}`
  } else {
    const obj = {}; roles.forEach(r => obj[r.token.replace('--', '')] = { value: getHexForRole(r.id) });
    return JSON.stringify(obj, null, 2);
  }
})
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(113, 113, 122, 0.2);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(113, 113, 122, 0.4);
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Animations */
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.98);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes staggerReveal {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-up {
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  opacity: 0;
}

.animate-slide-down {
  animation: slideDown 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-scale-in {
  animation: scaleIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-stagger-1 {
  animation: staggerReveal 0.5s ease-out forwards;
}

.animate-stagger-2 {
  animation: staggerReveal 0.5s ease-out forwards;
  opacity: 0;
}

.tab-slide-enter-active,
.tab-slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.tab-slide-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.tab-slide-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}
</style>